/**
 * The language catalog, ported unchanged from legacy `lib/i18n/config.ts`.
 *
 * It mirrors the backend's `ai-translate` list, and the switcher, the product
 * schemas and the AI-translate settings page all read the same twelve entries.
 * Adding or reordering one here without the backend is how the two drift.
 */
export const LANGUAGE_CATALOG = [
  { code: 'sk', label: 'Slovenčina', flag: '🇸🇰', english: 'Slovak' },
  { code: 'cs', label: 'Čeština', flag: '🇨🇿', english: 'Czech' },
  { code: 'en', label: 'English', flag: '🇬🇧', english: 'English' },
  { code: 'hu', label: 'Magyar', flag: '🇭🇺', english: 'Hungarian' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪', english: 'German' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱', english: 'Polish' },
  { code: 'uk', label: 'Українська', flag: '🇺🇦', english: 'Ukrainian' },
  { code: 'ro', label: 'Română', flag: '🇷🇴', english: 'Romanian' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹', english: 'Italian' },
  { code: 'fr', label: 'Français', flag: '🇫🇷', english: 'French' },
  { code: 'es', label: 'Español', flag: '🇪🇸', english: 'Spanish' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺', english: 'Russian' },
] as const

export type LanguageCode = (typeof LANGUAGE_CATALOG)[number]['code']

export const DEFAULT_LANGUAGE: LanguageCode = 'sk'

/** Languages with a curated bundle; the rest fall back through i18next. */
export const SHIPPED_LOCALES = ['sk', 'cs', 'en', 'hu', 'de', 'pl', 'uk'] as const

export function langInfo(code: string | null | undefined) {
  return LANGUAGE_CATALOG.find((language) => language.code === code) ?? LANGUAGE_CATALOG[0]
}

/**
 * The flag a language is drawn with — a **country** code, which is not the language code.
 *
 * `cs` flies the Czech flag, `en` the British one, `uk` the Ukrainian. Getting this from the language
 * code alone gives `uk` → United Kingdom, which is the wrong country for Ukrainian.
 *
 * It lives beside the catalogue because both apps draw the same switcher list: the customer chooses the
 * language they read the menu in, the manager the one they run the till in, and they write the same
 * `fk_lang` key on a shared browser.
 *
 * Self-hosted SVGs rather than the regional-indicator emoji: Windows renders those as bare letters —
 * "SK SK" — which is why legacy stopped using them.
 */
export const LANGUAGE_TO_COUNTRY: Record<string, string> = {
  sk: 'sk',
  cs: 'cz',
  en: 'gb',
  hu: 'hu',
  de: 'de',
  pl: 'pl',
  uk: 'ua',
  ro: 'ro',
  it: 'it',
  fr: 'fr',
  es: 'es',
  ru: 'ru',
}

/**
 * The languages this franchise offers — `FRONTEND.md` §9.92.
 *
 * The owner picks them in *AI preklad*; `GET /settings/basic-public` answers them as
 * `autoTranslateLanguages`, and until now nothing read it: both switchers offered every shipped
 * bundle whatever the franchise had chosen.
 *
 * **A chosen language with no bundle is not offered.** This franchise has picked `it`, which ships
 * no translations — offering it would hand a customer an untranslated screen with no way to tell
 * that from a broken one.
 *
 * **The default is always offered**, even where somebody has unticked it, because a switcher that
 * omits the language the app opens in strands whoever is already in it.
 *
 * **Nothing chosen means everything shipped**, which is what both apps did before this existed: a
 * settings read that did not answer must not cut a working menu down to one language.
 */
export function offeredLanguages(
  chosen: readonly string[] | null | undefined,
  defaultLanguage?: string | null,
): string[] {
  const shipped = SHIPPED_LOCALES as readonly string[]
  const catalogued = LANGUAGE_CATALOG.map((language) => language.code as string)
  const wanted = (chosen ?? []).map((code) => code.trim()).filter((code) => code !== '')
  // §9.376, FB-670 - **no longer intersected with `SHIPPED_LOCALES`.** The owner enabled Italiano in
  // *Preklady* and it appeared in neither switcher, because this filtered their choice down to the
  // seven bundles this build ships. Legacy does not: its switcher is
  // `LANGUAGE_CATALOG.filter(l => enabled.includes(l.code))`, and legacy ships **the same seven**
  // bundles - so an Italian customer there has always read an AI-translated menu with Slovak
  // buttons. The catalogue is still the boundary: a code nobody has heard of is not a language.
  const offered = wanted.filter((code) => catalogued.includes(code))

  // Asked *before* the default is added, or an empty choice would answer the default alone — which
  // is a switcher with one entry rather than the untouched menu this is meant to leave behind.
  if (offered.length === 0) return [...shipped]

  const fallbackDefault = (defaultLanguage ?? '').trim()
  if (
    fallbackDefault !== '' &&
    catalogued.includes(fallbackDefault) &&
    !offered.includes(fallbackDefault)
  ) {
    offered.push(fallbackDefault)
  }

  // Catalogue order, not the order they were ticked in: a switcher that reshuffles when somebody
  // edits the settings is one people lose their place in. **Over the catalogue rather than over the
  // shipped seven** - §9.376: filtering the answer through `SHIPPED_LOCALES` here would put the
  // intersection back at the last line after removing it at the first.
  return catalogued.filter((code) => offered.includes(code))
}
